import type { HelpSection } from './helpContent.types'

export const expertHelpContent: HelpSection[] = [
  {
    title: 'Content Library',
    subSections: [
      {
        entries: [
          {
            question: 'What is the Content Library?',
            answer:
              'The Content Library is where all Content Modules for your organization are stored and managed. Program Managers use it to review, publish, and organize modules created by themselves or their Experts.',
          },
          {
            question: 'What do the Content Library tabs mean?',
            answer:
              'The tabs organize modules by ownership or status:\n\nAll Modules – All modules created by you or your Experts\n\nMy Modules – Modules you own\n\nAwaiting Approval – Modules submitted by Experts for review\n\nPublished – Modules currently available to End Users\n\nIn Progress – Modules in draft or testing\n\nHelp Modules – Sidekick help content',
          },
          {
            question: 'What do the Content Module statuses mean?',
            answer:
              'Draft – The module is being created or edited. The owner can make changes to the Content Module.\n\nTesting – The module is being previewed in the app. Only the owner is able to preview the Content Module in the app.\n\nPending – Submitted for Program Manager review. Program Managers and Experts can preview the Content Module in the app. Program Managers can choose to either Publish the Content Module or put it back into pending or draft status for the Expert to make changes.\n\nPublished – Available to End Users. No changes can be made to the content at this point.',
          },
          {
            question: 'Will my module be visible to End Users when I save it?',
            answer:
              'No. Saving a module only stores it as a draft. A module becomes available to End Users only when it is published.',
          },
          {
            question: 'How do I publish a module?',
            answer:
              'After testing your module, click Submit for Approval. This will send your Content Module to the Program Manager for approval. Program Managers can review and publish your Content Module. Once published, the module becomes available to all End Users.',
          },
          {
            question: 'Why can’t I edit some modules?',
            answer:
              'Only the owner of a Content Module can edit it. Other users can view modules but cannot modify them. Published Content Modules also cannot be edited.',
          },
          {
            question: 'Can I remove a module from the app?',
            answer:
              'Experts cannot unpublish Content Modules once they are published. You must ask a Program Manager to move the module back to pending or draft status.',
          },
        ],
      },
    ],
  },
  {
    title: 'Content Module Creation',
    subSections: [
      {
        entries: [
          {
            question: 'How do I create a new Content Module?',
            answer:
              'Go to the Content Library and click New Module in the top right corner. This opens the Content Module creation workflow.',
          },
          {
            question: 'What content can I add to a Content Module?',
            answer:
              'You can add content in three ways:\n\nUpload a file (documents, images, audio, or video)\n\nProvide a URL (articles, pages, YouTube videos)\n\nManually enter text\n\nEach Content Module must include at least one file.',
          },
          {
            question: 'What file types can I upload?',
            answer:
              'Supported file formats include:\n\npng, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, pdf, pptx, and docx.',
          },
          {
            question: 'Can I use YouTube Links?',
            answer:
              'Yes. Begin by entering the link into the “Provide URL” field, and press enter. Once it has processed, you will need to manually enter a transcript. You can copy and paste the transcript from YouTube. The system will then generate a title and a description. Save your work, and then proceed as with the other files.',
          },
          {
            question: 'Why does the system generate suggestions for fields like titles or introductions?',
            answer:
              'The system uses AI to analyze your content and suggest fields such as:\n\nFile title\n\nModule introduction\n\nFAQs\n\nOutline\n\nThese are suggestions only. You can approve the AI generated suggestions, edit them, regenerate them, or replace them with your own text.',
          },
          {
            question: 'What are FAQs used for in a Content Module?',
            answer:
              'FAQs help the Sidekick respond quickly to common questions and provide helpful prompts if users are unsure what to ask. They also improve how the Sidekick surfaces relevant information.',
          },
          {
            question: 'What is a Delivery Style?',
            answer:
              'Delivery Style controls how the Sidekick presents the content to users. Available templates include:\n\nLecture – structured walkthrough of the material\n\nDiscovery – user-led exploration through questions\n\nMedia Mode – focuses on the primary (first) media file, and keeps it onscreen\n\nYou can customize the behavior after selecting a template.',
          },
          {
            question: 'Can I change the delivery style if I don’t like it?',
            answer:
              'Yes! You can switch the delivery style after your initial selection as long as your Content Module is in draft status. You can edit the delivery style or you can select a different template by clicking on Templates and then selecting the one you want to try.',
          },
          {
            question: 'Why won’t my Content Module save?',
            answer:
              'The most common reasons are:\n\nRequired fields are missing\n\nAI-generated content has not been accepted\n\nThe system will show an error and allow you to scroll to the problem field.',
          },
        ],
      },
    ],
  },
  {
    title: 'Content Module Creation – Best Practices',
    subSections: [
      {
        entries: [
          {
            question: 'What makes a good Content Module title?',
            answer:
              'Use a clear, descriptive title that helps users immediately understand what the module is about. Avoid vague titles and focus on the topic or outcome the user will learn.\n\nExample:\nGood: How to Apply for FAFSA\nPoor: Financial Help',
          },
          {
            question: 'What makes a good Module Introduction?',
            answer:
              'A strong Module Introduction should:\n\nClearly explain what the module covers\n\nBe short (1–2 sentences)\n\nEnd with a question that encourages the user to engage\n\nExample:\n"This module explains how to complete your FAFSA application step by step. Would you like help getting started?"',
          },
          {
            question: 'How much content should I include in a module?',
            answer:
              'Include enough content to fully explain the topic. Multiple files can be added to provide supporting information, videos, or documents that the Sidekick can reference when needed.',
          },
          {
            question: 'How can I improve the quality of AI-generated suggestions?',
            answer:
              'Click the arrow next to the “Ask Sidekick” button. Choose the Guide option. Provide detailed instructions for the AI so it can alter the output to suit your needs.',
          },
          {
            question: 'How Should Content Be Organized Across Sidekicks?',
            answer:
              'It is recommended that each Sidekick have one large Content Module that contains all materials related to a specific subject, rather than distributing similar information across multiple Sidekicks. This helps End Users get the most relevant information as quickly as possible.\n\nHowever, a Sidekick can have multiple Content Modules when they cover clearly distinct subject areas or are designed for different media types.',
          },
        ],
      },
    ],
  },
  {
    title: 'Testing and Publishing Content Modules – Top FAQs',
    subSections: [
      {
        entries: [
          {
            question: 'How do I test a Content Module before publishing it?',
            answer:
              'After saving your Content Module, change the module status to Test in App in the Status column of the Portal’s Content Library.\n\nYou can then open the mobile app and interact with the module as an End User to make sure the content and responses work as expected.\n\nIn the mobile app:\n\nOpen the menu in the bottom-right corner.\n\nSelect Change Role.\n\nChoose End User – Test.\n\nThis allows you to experience the module from the End User perspective before it is published.',
          },
          {
            question: 'Who can see a module while it is being tested?',
            answer:
              'Modules in Test in App status are only visible to the module owner during testing. They are not visible to other End Users, Experts, or Program Managers.',
          },
          {
            question: 'What should I check during testing?',
            answer:
              'During testing, you should confirm that:\n\nThe Sidekick responses are accurate\n\nContent appears when expected\n\nFAQs work correctly\n\nMedia or supplemental resources display properly\n\nThe conversation flow makes sense',
          },
          {
            question: 'How do Experts submit a Module for approval?',
            answer:
              'Once testing is complete:\n\nGo to the Content Library.\n\nClick the Status next to your module.\n\nSelect Submit for Approval.\n\nThe module will then be sent to a Program Manager for review.',
          },
          {
            question: 'What happens after an Expert submits a Module for approval?',
            answer:
              'A Program Manager reviews the module and decides whether to publish it. While a Content Module is in pending status, the owner of the Content Module and Program Managers can experience it if they switch to End User - Test role in the app. Once published, the Module becomes available to all End Users.',
          },
          {
            question: 'How do I publish a Module?',
            answer:
              'After testing your Module, click Submit for Approval, it will be sent to the Program Manager for approval. Program Managers can review and publish your Content Module. Once published, the Module becomes available to all End Users.',
          },
          {
            question: 'Can a published Module be edited?',
            answer:
              'Not in the Published state. Published Modules must first be moved back to Draft before they can be edited. After making changes, the Module must be submitted and published again.',
          },
          {
            question: 'What if I need to make changes during testing?',
            answer:
              'If you need to update the Module, change its status to Back to Draft, make your edits, save the Module, and then test it again.',
          },
          {
            question: 'Will my Module become available when I save it?',
            answer:
              'No. Saving a Module only stores it as a Draft. A Module becomes available to End Users only after a Program Manager publishes it.',
          },
        ],
      },
    ],
  },
  {
    title: 'Feedback – Top FAQs',
    subSections: [
      {
        entries: [
          {
            question: 'What is the Feedback page used for?',
            answer:
              'The Feedback page allows Program Managers and Experts to review feedback submitted by End Users about Content Modules. This helps teams understand user sentiment, identify issues, and improve content.',
          },
          {
            question: 'What feedback can I see?',
            answer: 'As an Expert, you can see feedback on Content Modules that you own.',
          },
          {
            question: 'What do the summary metrics at the top of the page show?',
            answer:
              'The summary panel provides a quick overview of feedback activity:\n\nModules – Number of Modules that have received feedback\n\nComments – Total written comments from End Users\n\nThumbs Up – Total positive feedback\n\nThumbs Down – Total negative feedback',
          },
          {
            question: 'What do the feedback ratings mean?',
            answer:
              'Feedback ratings indicate the user’s sentiment about the response they received:\n\nThumbs Up – Positive feedback\n\nThumbs Down – Negative feedback\n\nQuestion mark (?) – No rating was provided',
          },
          {
            question: 'How do I view more details about a feedback entry?',
            answer:
              'Click the Detail icon in the row.\n\nThis opens the Feedback Details panel, which shows the user comment, the Sidekick response they reacted to, and the surrounding conversation history.',
          },
          {
            question: 'What does the conversation history show?',
            answer:
              'The conversation history displays the interaction between the user and the Sidekick leading up to the feedback. Messages appear in chronological order and are labeled by speaker.',
          },
          {
            question: 'Can I filter or sort feedback results?',
            answer:
              'Yes. You can:\n\nSort by Date, Module, Rating, or Tag\n\nClick the up and down arrows to do a simple search\n\nApply filters to narrow results\n\nClick the funnel icon at the top right of the feedback table or click the three dot icon next to the column header and then select “Filter by”\n\nShow or hide columns\n\nClick the three dots next to the column header and then select “Hide column”\n\nAdjust table density or view the table in fullscreen\n\nShow the table fullscreen by tapping the icon on the far right above the table\n\nTap on the single-column horizontal lines to adjust the density',
          },
          {
            question: 'What happens to feedback if a Module or topic is deleted?',
            answer: 'Feedback entries remain in the system even if the associated Module or topic is later removed.',
          },
        ],
      },
    ],
  },
  {
    title: 'Metrics',
    subSections: [
      {
        entries: [
          {
            question: 'What is the Metrics page used for?',
            answer:
              'The Metrics page shows how End Users are interacting with the Sidekick Platform. It provides anonymized statistics about system usage, including conversations, messages, and how often topics or Content Modules are used.',
          },
          {
            question: 'What tabs are available on the Metrics page?',
            answer:
              'The Metrics page includes several tabs:\n\nOverview – Overall platform usage statistics\n\nTopics – Usage metrics for specific topics (if topics are enabled)\n\nContent Modules – Usage metrics for individual Modules',
          },
          {
            question: 'Is the data on the Metrics page real-time?',
            answer: 'No. Metrics data is updated several times each day, but it is not displayed in real time.',
          },
          {
            question: 'Does the Metrics page show information about individual users?',
            answer:
              'No. All metrics are aggregated and anonymized, so the page shows overall usage trends rather than information about individual End Users.',
          },
          {
            question: 'What information does the Overview tab show?',
            answer:
              'The Overview tab provides a snapshot of overall platform activity, including:\n\nTotal conversations with Sidekicks\n\nA conversation is all the messages sent between a user and a specific Sidekick until either the user moves to a different Sidekick OR 48 hours have elapsed with no user activity.\n\nTotal messages sent by End Users\n\nAverage daily messages\n\nUnique conversations\n\nNumber of modules users interacted with\n\nIt also includes charts showing daily active users, messages sent, and Content Module conversations over time.',
          },
          {
            question: 'What metrics are available for Content Modules?',
            answer:
              'In the Metrics, the Content Modules tab shows usage metrics for a selected Module, including:\n\nUnique visitors\n\nTotal conversations\n\nMessages sent by End Users\n\nAverage conversation length\n\nLongest conversation\n\nThese pages also include charts showing how usage changes over time.',
          },
        ],
      },
    ],
  },
  {
    title: 'Channel Switching and Logout',
    subSections: [
      {
        entries: [
          {
            question: 'What does switching channels mean in the Sidekick Platform?',
            answer:
              'Switching channels allows users who have access to multiple organizations to move between different Sidekick environments.',
          },
          {
            question: 'I have been invited to multiple channels. Can I switch between them?',
            answer:
              'Yes. If your account has access to multiple channels, you can switch between them within the Sidekick Portal. Switching channels allows you to view and manage the content, users, and settings for each organization you have access to.\n\nTo switch channels, click your profile icon in the top right corner in the Portal and choose the channel you want to access. The Portal will then update to display the data, settings, and content associated with that specific channel.',
          },
          {
            question: 'How do I switch between channels in the Sidekick Portal?',
            answer:
              'Click your profile icon in the top right corner in the Portal to choose another channel available to your account.',
          },
          {
            question: 'How do I switch channels in the app while I am testing?',
            answer:
              'If your account has access to multiple channels, you can switch between them directly in the mobile app while testing.\n\nTo switch channels in the app:\n\nTap the three-dot menu in the lower right corner of the app.\n\nSelect Change Channel.\n\nChoose the channel you want to switch to from the list.\n\nThe app will refresh and load the content, Sidekicks, and modules associated with that channel.\n\nThis allows you to test and experience the app exactly as End Users would within each channel you have access to.',
          },
          {
            question: 'How do I log out of the Sidekick Portal?',
            answer:
              'To log out of the Sidekick Portal, click your profile icon in the top right corner select Log Out.',
          },
          {
            question: 'Will logging out affect my saved work in the Portal?',
            answer: 'No. Any saved changes remain stored in the system.',
          },
          {
            question: 'What should I do if I cannot access my account?',
            answer:
              'Contact your organization’s administrator or Sidekick support for assistance. support@sidekickwellness.com',
          },
        ],
      },
    ],
  },
]
